/**
 * The conversation's own sentences, in the customer's language.
 *
 * WHY THIS EXISTS
 *   The assistant already answers in whatever language the customer writes or speaks. The GUIDED flow did
 *   not: the moment a customer tapped a choice, the sale advanced locally and asked its next question from
 *   a hardcoded English string. A sale that opened in Hindi - assistant replying in Hindi - switched to
 *   "Which one do you have?" at the first tap, and stayed English for the rest of the questions.
 *
 * WHAT IS TRANSLATED, AND WHAT IS NOT
 *   Only these fixed sentences. NOT the choices beside them: a device name is catalogue data that must
 *   match what Cashit holds, and the condition, fault and accessory labels are the business's own words
 *   from its database, with ids the valuation service is given back. Translating those in the browser
 *   would mean the customer reads one thing and the price is computed from another.
 *
 *   So a Hindi sale reads as a Hindi question above English option labels. That is the honest halfway
 *   point available without the translations living in Cashit's own data, and it is a large improvement on
 *   a question nobody asked in a language they were not using.
 *
 * ACCURACY
 *   These are machine translations. They read correctly and say the right thing, but they have not been
 *   checked by a native speaker of each language, and a selling flow is worth that check. The structure is
 *   a plain table so a correction is a one-line edit, and any language with no entry simply falls back to
 *   English rather than showing a gap.
 */

export type PhraseKey =
  | 'category'
  | 'didYouMeanOne'
  | 'didYouMeanMany'
  | 'whichOfThese'
  | 'exactModel'
  | 'exactModelPlaceholder'
  | 'whichOneDoYouHave'
  | 'whichConfiguration'
  | 'confirmSpecification'
  | 'howOld'
  | 'howDoesItLook'
  | 'thatIsItsCondition'
  | 'noMarksAtAll'
  | 'anythingNotWorking'
  | 'thatIsAllThatIsWrong'
  | 'everythingWorks'
  | 'whichDoYouStillHave'
  | 'thatIsWhatIHave'
  | 'iHaveNoneOfThese'
  | 'pincode'
  | 'pincodePlaceholder'
  // The rest of the conversation: the price, verifying a number, and confirming the sale.
  | 'quote'
  | 'composerPlaceholder'
  | 'verifyHeading'
  | 'verifyCopy'
  | 'mobileLabel'
  | 'mobilePlaceholder'
  | 'sendCode'
  | 'codeHeading'
  | 'codeCopy'
  | 'codeLabel'
  | 'verifyAndContinue'
  | 'resendIn'
  | 'resendCode'
  | 'changeNumber'
  | 'registerHeading'
  | 'registerCopy'
  | 'fullName'
  | 'emailOptional'
  | 'createAccount'
  // Confirming the sale, and what is said once it exists.
  | 'confirmHeading'
  | 'confirmCopy'
  | 'account'
  | 'mobile'
  | 'device'
  | 'cashitPays'
  | 'placedVia'
  | 'pickupAddress'
  | 'differentAddress'
  | 'pickupPincode'
  | 'area'
  | 'address'
  | 'landmarkOptional'
  | 'whenCollect'
  | 'slotMorning'
  | 'slotAfternoon'
  | 'slotEvening'
  | 'today'
  | 'tomorrow'
  | 'visitNote'
  | 'confirmButton'
  | 'doneMessage'
  | 'doneDetail'
  | 'trackSale'
  | 'trackSaleHint'

type Phrases = Record<PhraseKey, string>

const en: Phrases = {
  category: 'What would you like to sell? Pick a category, or just type what you have.',
  didYouMeanOne: 'Do you mean this one?',
  didYouMeanMany: 'Do you mean one of these?',
  whichOfThese: 'Which one of these is it?',
  exactModel: 'What is the exact model? Brand and model name is enough.',
  exactModelPlaceholder: 'e.g. iPhone 13 Pro 256 GB',
  whichOneDoYouHave: 'Which one do you have?',
  whichConfiguration: 'Which configuration is yours?',
  confirmSpecification:
    "Let me confirm the specification. This is what Cashit has for this model - change anything that is different on yours.",
  howOld: 'How old is the device?',
  howDoesItLook: 'How does it look? Choose everything that applies.',
  thatIsItsCondition: 'That is its condition',
  noMarksAtAll: 'No marks at all',
  anythingNotWorking: 'Is anything not working? Choose everything that applies, or say nothing is wrong.',
  thatIsAllThatIsWrong: 'That is all that is wrong',
  everythingWorks: 'Everything works',
  whichDoYouStillHave: 'Which of these do you still have?',
  thatIsWhatIHave: 'That is what I have',
  iHaveNoneOfThese: 'I have none of these',
  pincode: 'Last one: what is your six-digit pickup pincode?',
  pincodePlaceholder: 'Enter a 6-digit pincode',
  quote: 'Cashit will pay {amount} rupees for it, subject to inspection. Shall we go ahead?',
  composerPlaceholder: 'Type a device name, or ask me anything…',
  verifyHeading: 'Verify your mobile number',
  verifyCopy:
    "Your quote is ready. We'll send a one-time code to confirm it is you, then create the sale request in your Cashit account. Everything you have told me is saved.",
  mobileLabel: 'Mobile number',
  mobilePlaceholder: '10-digit mobile number',
  sendCode: 'Send code',
  codeHeading: 'Enter the code',
  codeCopy: 'We sent a code to {mobile}. It is valid for {minutes} minutes.',
  codeLabel: 'One-time code',
  verifyAndContinue: 'Verify and continue',
  resendIn: 'Resend in {seconds}s',
  resendCode: 'Resend code',
  changeNumber: 'Change number',
  registerHeading: 'Create your Cashit account',
  registerCopy: '{mobile} is new to Cashit. Just your name, and this is the same account you will use on cashit.in.',
  fullName: 'Full name',
  emailOptional: 'Email (optional)',
  createAccount: 'Create account and continue',
  confirmHeading: 'Confirm your sale request',
  confirmCopy:
    'Nothing is created until you confirm. Cashit will contact you to arrange the pickup, and the final amount is confirmed after inspection.',
  account: 'Account',
  mobile: 'Mobile',
  device: 'Device',
  cashitPays: 'Cashit pays',
  placedVia: 'Sale placed via',
  pickupAddress: 'Pickup address',
  differentAddress: 'Collect from a different address',
  pickupPincode: 'Pickup pincode',
  area: 'Area',
  address: 'Address',
  landmarkOptional: 'Landmark (optional)',
  whenCollect: 'When should we collect it?',
  slotMorning: 'Morning · 10am – 1pm',
  slotAfternoon: 'Afternoon · 1pm – 4pm',
  slotEvening: 'Evening · 4pm – 7pm',
  today: 'Today',
  tomorrow: 'Tomorrow',
  visitNote: 'Cashit will confirm the visit with you — this tells the agent when suits you best.',
  confirmButton: 'Confirm and create my sale request',
  doneMessage: 'Done. Your sale request is {leadNumber}, and Cashit will be in touch to arrange the pickup.',
  doneDetail:
    'Your sale request {leadNumber} is with Cashit, placed via {source}. Next, an agent is assigned and arranges the pickup; after that Cashit checks the device and pays you.',
  trackSale: 'Track this sale',
  trackSaleHint: 'See its status and what happens next',
}

const hi: Phrases = {
  category: 'आप क्या बेचना चाहते हैं? कोई श्रेणी चुनें, या जो आपके पास है वह लिख दें।',
  didYouMeanOne: 'क्या आपका मतलब यह है?',
  didYouMeanMany: 'क्या आपका मतलब इनमें से किसी एक से है?',
  whichOfThese: 'इनमें से कौन-सा है?',
  exactModel: 'सटीक मॉडल कौन-सा है? ब्रांड और मॉडल का नाम काफ़ी है।',
  exactModelPlaceholder: 'जैसे iPhone 13 Pro 256 GB',
  whichOneDoYouHave: 'आपके पास इनमें से कौन-सा है?',
  whichConfiguration: 'आपका कॉन्फ़िगरेशन कौन-सा है?',
  confirmSpecification:
    'आइए स्पेसिफिकेशन की पुष्टि कर लें। Cashit के पास इस मॉडल के लिए यही दर्ज है - जो आपके डिवाइस में अलग हो, उसे बदल दें।',
  howOld: 'डिवाइस कितना पुराना है?',
  howDoesItLook: 'यह दिखने में कैसा है? जो भी लागू हो, सब चुनें।',
  thatIsItsCondition: 'इसकी हालत यही है',
  noMarksAtAll: 'कोई निशान नहीं है',
  anythingNotWorking: 'क्या कुछ काम नहीं कर रहा? जो भी लागू हो चुनें, या बताएं कि सब ठीक है।',
  thatIsAllThatIsWrong: 'बस इतनी ही खराबी है',
  everythingWorks: 'सब कुछ काम करता है',
  whichDoYouStillHave: 'इनमें से क्या-क्या अब भी आपके पास है?',
  thatIsWhatIHave: 'मेरे पास यही है',
  iHaveNoneOfThese: 'इनमें से कुछ भी नहीं है',
  pincode: 'आख़िरी सवाल: पिकअप के लिए आपका छह अंकों का पिनकोड क्या है?',
  pincodePlaceholder: '6 अंकों का पिनकोड डालें',
  quote: "इसके लिए Cashit {amount} रुपये देगा, जाँच के बाद पुष्टि होगी। क्या हम आगे बढ़ें?",
  composerPlaceholder: "डिवाइस का नाम लिखें, या कुछ भी पूछें…",
  verifyHeading: "अपना मोबाइल नंबर सत्यापित करें",
  verifyCopy: "आपका दाम तैयार है। हम पुष्टि के लिए एक बार का कोड भेजेंगे, फिर आपके Cashit खाते में बिक्री का अनुरोध बना देंगे। आपने जो बताया है, सब सुरक्षित है।",
  mobileLabel: "मोबाइल नंबर",
  mobilePlaceholder: "10 अंकों का मोबाइल नंबर",
  sendCode: "कोड भेजें",
  codeHeading: "कोड डालें",
  codeCopy: "हमने {mobile} पर कोड भेजा है। यह {minutes} मिनट तक मान्य है।",
  codeLabel: "एक बार का कोड",
  verifyAndContinue: "सत्यापित करें और आगे बढ़ें",
  resendIn: "{seconds} सेकंड में दोबारा भेजें",
  resendCode: "कोड दोबारा भेजें",
  changeNumber: "नंबर बदलें",
  registerHeading: "अपना Cashit खाता बनाएँ",
  registerCopy: "{mobile} Cashit पर नया है। बस अपना नाम बताएँ - यही खाता आप cashit.in पर भी इस्तेमाल करेंगे।",
  fullName: "पूरा नाम",
  emailOptional: "ईमेल (वैकल्पिक)",
  createAccount: "खाता बनाएँ और आगे बढ़ें",
  confirmHeading: "अपनी बिक्री की पुष्टि करें",
  confirmCopy: "जब तक आप पुष्टि नहीं करते, कुछ नहीं बनता। पिकअप तय करने के लिए Cashit आपसे संपर्क करेगा, और अंतिम रकम जाँच के बाद तय होगी।",
  account: "खाता",
  mobile: "मोबाइल",
  device: "डिवाइस",
  cashitPays: "Cashit देगा",
  placedVia: "बिक्री का माध्यम",
  pickupAddress: "पिकअप का पता",
  differentAddress: "किसी दूसरे पते से लेना है",
  pickupPincode: "पिकअप पिनकोड",
  area: "इलाक़ा",
  address: "पता",
  landmarkOptional: "लैंडमार्क (वैकल्पिक)",
  whenCollect: "हम इसे कब लेने आएँ?",
  slotMorning: "सुबह · 10 – 1 बजे",
  slotAfternoon: "दोपहर · 1 – 4 बजे",
  slotEvening: "शाम · 4 – 7 बजे",
  today: "आज",
  tomorrow: "कल",
  visitNote: "Cashit आपसे विज़िट की पुष्टि करेगा — इससे एजेंट को पता चलता है कि आपको कब सुविधा है।",
  confirmButton: "पुष्टि करें और बिक्री का अनुरोध बनाएँ",
  doneMessage: "हो गया। आपकी बिक्री का अनुरोध {leadNumber} है, और पिकअप तय करने के लिए Cashit संपर्क करेगा।",
  doneDetail: "आपका अनुरोध {leadNumber} Cashit के पास है, {source} से भेजा गया। आगे एक एजेंट नियुक्त होगा और पिकअप करेगा; उसके बाद Cashit डिवाइस जाँचकर आपको भुगतान करेगा।",
  trackSale: "इस बिक्री को ट्रैक करें",
  trackSaleHint: "स्थिति और आगे क्या होगा देखें",
}

const mr: Phrases = {
  ...en,
  category: 'तुम्हाला काय विकायचे आहे? एक श्रेणी निवडा, किंवा तुमच्याकडे जे आहे ते लिहा.',
  whichOneDoYouHave: 'तुमच्याकडे यापैकी कोणते आहे?',
  howOld: 'डिव्हाइस किती जुने आहे?',
  howDoesItLook: 'ते दिसायला कसे आहे? लागू होणारे सर्व निवडा.',
  anythingNotWorking: 'काही काम करत नाही का? लागू होणारे सर्व निवडा, किंवा सर्व ठीक आहे असे सांगा.',
  everythingWorks: 'सर्व काही काम करते',
  whichDoYouStillHave: 'यापैकी काय अजूनही तुमच्याकडे आहे?',
  iHaveNoneOfThese: 'यापैकी काहीही नाही',
  pincode: 'शेवटचा प्रश्न: पिकअपसाठी तुमचा सहा अंकी पिनकोड काय आहे?',
  quote: "यासाठी Cashit {amount} रुपये देईल, तपासणीनंतर निश्चित होईल. आपण पुढे जाऊया का?",
  composerPlaceholder: "डिव्हाइसचे नाव लिहा, किंवा काहीही विचारा…",
  verifyHeading: "तुमचा मोबाइल नंबर सत्यापित करा",
  mobilePlaceholder: "10 अंकी मोबाइल नंबर",
  sendCode: "कोड पाठवा",
  codeHeading: "कोड टाका",
  codeCopy: "आम्ही {mobile} वर कोड पाठवला आहे. तो {minutes} मिनिटे वैध आहे.",
  codeLabel: "एक वेळचा कोड",
  verifyAndContinue: "सत्यापित करा आणि पुढे जा",
  resendIn: "{seconds} सेकंदात पुन्हा पाठवा",
  resendCode: "कोड पुन्हा पाठवा",
  changeNumber: "नंबर बदला",
  registerHeading: "तुमचे Cashit खाते तयार करा",
  fullName: "पूर्ण नाव",
  emailOptional: "ईमेल (ऐच्छिक)",
  createAccount: "खाते तयार करा आणि पुढे जा",
  confirmHeading: "तुमच्या विक्रीची पुष्टी करा",
  account: "खाते",
  device: "डिव्हाइस",
  cashitPays: "Cashit देईल",
  placedVia: "विक्रीचे माध्यम",
  pickupAddress: "पिकअपचा पत्ता",
  differentAddress: "दुसऱ्या पत्त्यावरून घ्यायचे आहे",
  area: "भाग",
  address: "पत्ता",
  landmarkOptional: "लँडमार्क (ऐच्छिक)",
  whenCollect: "आम्ही ते कधी घ्यायला येऊ?",
  slotMorning: "सकाळी · 10 – 1",
  slotAfternoon: "दुपारी · 1 – 4",
  slotEvening: "संध्याकाळी · 4 – 7",
  tomorrow: "उद्या",
  confirmButton: "पुष्टी करा आणि विक्रीची विनंती तयार करा",
  trackSale: "ही विक्री ट्रॅक करा",
  trackSaleHint: "स्थिती आणि पुढे काय होईल पहा",
  didYouMeanOne: "तुम्हाला हेच म्हणायचे आहे का?",
  didYouMeanMany: "तुम्हाला यापैकी एक म्हणायचे आहे का?",
  whichOfThese: "यापैकी कोणते आहे?",
  exactModel: "नेमके कोणते मॉडेल? ब्रँड आणि मॉडेलचे नाव पुरेसे आहे.",
  exactModelPlaceholder: "उदा. iPhone 13 Pro 256 GB",
  whichConfiguration: "तुमचे कॉन्फिगरेशन कोणते आहे?",
  confirmSpecification: "तपशील तपासून घेऊ. Cashit कडे या मॉडेलसाठी हेच नोंदवलेले आहे - तुमच्या डिव्हाइसमध्ये वेगळे असेल ते बदला.",
  thatIsItsCondition: "त्याची स्थिती हीच आहे",
  noMarksAtAll: "कोणतेही ओरखडे नाहीत",
  thatIsAllThatIsWrong: "एवढाच बिघाड आहे",
  thatIsWhatIHave: "माझ्याकडे हेच आहे",
  pincodePlaceholder: "6 अंकी पिनकोड टाका",
  verifyCopy: "तुमची किंमत तयार आहे. खात्रीसाठी आम्ही एकदाच वापरायचा कोड पाठवू, मग तुमच्या Cashit खात्यात विक्रीची विनंती तयार करू. तुम्ही सांगितलेले सर्व जतन केले आहे.",
  mobileLabel: "मोबाइल नंबर",
  registerCopy: "{mobile} Cashit वर नवीन आहे. फक्त तुमचे नाव सांगा - हेच खाते तुम्ही cashit.in वर वापराल.",
  confirmCopy: "तुम्ही पुष्टी करेपर्यंत काहीही तयार होत नाही. पिकअप ठरवण्यासाठी Cashit संपर्क करेल, आणि अंतिम रक्कम तपासणीनंतर ठरेल.",
  mobile: "मोबाइल",
  pickupPincode: "पिकअप पिनकोड",
  today: "आज",
  visitNote: "Cashit तुमच्याशी भेटीची खात्री करेल — यामुळे एजंटला तुमची सोयीची वेळ कळते.",
  doneMessage: "झाले. तुमची विक्री विनंती {leadNumber} आहे, आणि पिकअपसाठी Cashit संपर्क करेल.",
  doneDetail: "तुमची विनंती {leadNumber} Cashit कडे आहे, {source} वरून पाठवली. पुढे एजंट नेमला जाईल आणि पिकअप करेल; नंतर Cashit डिव्हाइस तपासून तुम्हाला पैसे देईल.",
}

const ur: Phrases = {
  category: 'آپ کیا بیچنا چاہتے ہیں؟ کوئی زمرہ منتخب کریں، یا جو آپ کے پاس ہے وہ لکھ دیں۔',
  didYouMeanOne: 'کیا آپ کا مطلب یہ ہے؟',
  didYouMeanMany: 'کیا آپ کا مطلب ان میں سے کسی ایک سے ہے؟',
  whichOfThese: 'ان میں سے کون سا ہے؟',
  exactModel: 'بالکل کون سا ماڈل ہے؟ برانڈ اور ماڈل کا نام کافی ہے۔',
  exactModelPlaceholder: 'مثلاً iPhone 13 Pro 256 GB',
  whichOneDoYouHave: 'آپ کے پاس ان میں سے کون سا ہے؟',
  whichConfiguration: 'آپ کا کنفیگریشن کون سا ہے؟',
  confirmSpecification:
    'آئیے تفصیلات کی تصدیق کر لیں۔ Cashit کے پاس اس ماڈل کے لیے یہی درج ہے - جو آپ کے آلے میں مختلف ہو اسے بدل دیں۔',
  howOld: 'یہ آلہ کتنا پرانا ہے؟',
  howDoesItLook: 'یہ دیکھنے میں کیسا ہے؟ جو بھی لاگو ہو سب منتخب کریں۔',
  thatIsItsCondition: 'اس کی حالت یہی ہے',
  noMarksAtAll: 'کوئی نشان نہیں ہے',
  anythingNotWorking: 'کیا کچھ کام نہیں کر رہا؟ جو لاگو ہو منتخب کریں، یا بتائیں کہ سب ٹھیک ہے۔',
  thatIsAllThatIsWrong: 'بس اتنی ہی خرابی ہے',
  everythingWorks: 'سب کچھ کام کرتا ہے',
  whichDoYouStillHave: 'ان میں سے کیا کچھ اب بھی آپ کے پاس ہے؟',
  thatIsWhatIHave: 'میرے پاس یہی ہے',
  iHaveNoneOfThese: 'ان میں سے کچھ بھی نہیں',
  pincode: 'آخری سوال: پک اپ کے لیے آپ کا چھ ہندسوں کا پن کوڈ کیا ہے؟',
  pincodePlaceholder: '6 ہندسوں کا پن کوڈ درج کریں',
  quote: "اس کے لیے Cashit {amount} روپے دے گا، معائنے کے بعد تصدیق ہوگی۔ کیا ہم آگے بڑھیں؟",
  composerPlaceholder: "ڈیوائس کا نام لکھیں، یا کچھ بھی پوچھیں…",
  verifyHeading: "اپنا موبائل نمبر تصدیق کریں",
  verifyCopy: "آپ کی قیمت تیار ہے۔ ہم تصدیق کے لیے ایک بار کا کوڈ بھیجیں گے، پھر آپ کے Cashit اکاؤنٹ میں فروخت کی درخواست بنا دیں گے۔ آپ نے جو بتایا ہے سب محفوظ ہے۔",
  mobileLabel: "موبائل نمبر",
  mobilePlaceholder: "10 ہندسوں کا موبائل نمبر",
  sendCode: "کوڈ بھیجیں",
  codeHeading: "کوڈ درج کریں",
  codeCopy: "ہم نے {mobile} پر کوڈ بھیجا ہے۔ یہ {minutes} منٹ تک کارآمد ہے۔",
  codeLabel: "ایک بار کا کوڈ",
  verifyAndContinue: "تصدیق کریں اور آگے بڑھیں",
  resendIn: "{seconds} سیکنڈ میں دوبارہ بھیجیں",
  resendCode: "کوڈ دوبارہ بھیجیں",
  changeNumber: "نمبر بدلیں",
  registerHeading: "اپنا Cashit اکاؤنٹ بنائیں",
  registerCopy: "{mobile} Cashit پر نیا ہے۔ بس اپنا نام بتائیں - یہی اکاؤنٹ آپ cashit.in پر بھی استعمال کریں گے۔",
  fullName: "پورا نام",
  emailOptional: "ای میل (اختیاری)",
  createAccount: "اکاؤنٹ بنائیں اور آگے بڑھیں",
  confirmHeading: "اپنی فروخت کی تصدیق کریں",
  confirmCopy: "جب تک آپ تصدیق نہیں کرتے، کچھ نہیں بنتا۔ پک اپ طے کرنے کے لیے Cashit آپ سے رابطہ کرے گا، اور آخری رقم معائنے کے بعد طے ہوگی۔",
  account: "اکاؤنٹ",
  mobile: "موبائل",
  device: "ڈیوائس",
  cashitPays: "Cashit دے گا",
  placedVia: "فروخت کا ذریعہ",
  pickupAddress: "پک اپ کا پتہ",
  differentAddress: "کسی دوسرے پتے سے لینا ہے",
  pickupPincode: "پک اپ پن کوڈ",
  area: "علاقہ",
  address: "پتہ",
  landmarkOptional: "لینڈ مارک (اختیاری)",
  whenCollect: "ہم اسے کب لینے آئیں؟",
  slotMorning: "صبح · 10 – 1 بجے",
  slotAfternoon: "دوپہر · 1 – 4 بجے",
  slotEvening: "شام · 4 – 7 بجے",
  today: "آج",
  tomorrow: "کل",
  visitNote: "Cashit آپ سے وزٹ کی تصدیق کرے گا — اس سے ایجنٹ کو معلوم ہوتا ہے کہ آپ کو کب سہولت ہے۔",
  confirmButton: "تصدیق کریں اور فروخت کی درخواست بنائیں",
  doneMessage: "ہو گیا۔ آپ کی فروخت کی درخواست {leadNumber} ہے، اور پک اپ کے لیے Cashit رابطہ کرے گا۔",
  doneDetail: "آپ کی درخواست {leadNumber} Cashit کے پاس ہے، {source} سے بھیجی گئی۔ آگے ایک ایجنٹ مقرر ہوگا اور پک اپ کرے گا؛ اس کے بعد Cashit ڈیوائس جانچ کر ادائیگی کرے گا۔",
  trackSale: "اس فروخت کو ٹریک کریں",
  trackSaleHint: "حالت اور آگے کیا ہوگا دیکھیں",
}

const bn: Phrases = {
  ...en,
  category: 'আপনি কী বিক্রি করতে চান? একটি বিভাগ বেছে নিন, বা আপনার কাছে যা আছে লিখুন।',
  whichOfThese: 'এগুলোর মধ্যে কোনটি?',
  exactModel: 'ঠিক কোন মডেল? ব্র্যান্ড আর মডেলের নামই যথেষ্ট।',
  whichOneDoYouHave: 'আপনার কাছে কোনটি আছে?',
  whichConfiguration: 'আপনার কনফিগারেশন কোনটি?',
  howOld: 'ডিভাইসটি কত পুরনো?',
  howDoesItLook: 'দেখতে কেমন? প্রযোজ্য সবগুলি বেছে নিন।',
  thatIsItsCondition: 'এটাই এর অবস্থা',
  noMarksAtAll: 'কোনো দাগ নেই',
  anythingNotWorking: 'কিছু কি কাজ করছে না? প্রযোজ্য সব বেছে নিন, বা বলুন সব ঠিক আছে।',
  thatIsAllThatIsWrong: 'এটুকুই সমস্যা',
  everythingWorks: 'সবকিছু কাজ করে',
  whichDoYouStillHave: 'এগুলোর মধ্যে কী কী এখনও আপনার কাছে আছে?',
  thatIsWhatIHave: 'আমার কাছে এটাই আছে',
  iHaveNoneOfThese: 'এগুলোর কিছুই নেই',
  pincode: 'শেষ প্রশ্ন: পিকআপের জন্য আপনার ছয় অঙ্কের পিনকোড কী?',
  pincodePlaceholder: '৬ অঙ্কের পিনকোড লিখুন',
  quote: "এর জন্য Cashit {amount} টাকা দেবে, পরীক্ষার পরে নিশ্চিত হবে। আমরা এগোব?",
  composerPlaceholder: "ডিভাইসের নাম লিখুন, বা যা খুশি জিজ্ঞাসা করুন…",
  verifyHeading: "আপনার মোবাইল নম্বর যাচাই করুন",
  mobileLabel: "মোবাইল নম্বর",
  mobilePlaceholder: "১০ অঙ্কের মোবাইল নম্বর",
  sendCode: "কোড পাঠান",
  codeHeading: "কোড দিন",
  codeCopy: "আমরা {mobile} নম্বরে কোড পাঠিয়েছি। এটি {minutes} মিনিট বৈধ।",
  codeLabel: "এককালীন কোড",
  verifyAndContinue: "যাচাই করে এগিয়ে যান",
  resendIn: "{seconds} সেকেন্ডে আবার পাঠান",
  resendCode: "আবার কোড পাঠান",
  changeNumber: "নম্বর বদলান",
  registerHeading: "আপনার Cashit অ্যাকাউন্ট তৈরি করুন",
  fullName: "পুরো নাম",
  emailOptional: "ইমেল (ঐচ্ছিক)",
  createAccount: "অ্যাকাউন্ট তৈরি করে এগিয়ে যান",
  confirmHeading: "আপনার বিক্রির অনুরোধ নিশ্চিত করুন",
  confirmCopy: "আপনি নিশ্চিত না করা পর্যন্ত কিছুই তৈরি হয় না। পিকআপের জন্য Cashit যোগাযোগ করবে, এবং চূড়ান্ত অঙ্ক পরীক্ষার পরে ঠিক হবে।",
  account: "অ্যাকাউন্ট",
  mobile: "মোবাইল",
  device: "ডিভাইস",
  cashitPays: "Cashit দেবে",
  placedVia: "বিক্রির মাধ্যম",
  pickupAddress: "পিকআপের ঠিকানা",
  differentAddress: "অন্য ঠিকানা থেকে নিতে হবে",
  pickupPincode: "পিকআপ পিনকোড",
  area: "এলাকা",
  address: "ঠিকানা",
  landmarkOptional: "ল্যান্ডমার্ক (ঐচ্ছিক)",
  whenCollect: "কখন নিতে আসব?",
  slotMorning: "সকাল · ১০ – ১টা",
  slotAfternoon: "দুপুর · ১ – ৪টা",
  slotEvening: "সন্ধ্যা · ৪ – ৭টা",
  today: "আজ",
  tomorrow: "আগামীকাল",
  confirmButton: "নিশ্চিত করে অনুরোধ তৈরি করুন",
  trackSale: "এই বিক্রি ট্র্যাক করুন",
  trackSaleHint: "অবস্থা ও পরবর্তী ধাপ দেখুন",
  didYouMeanOne: "আপনি কি এটির কথা বলছেন?",
  didYouMeanMany: "আপনি কি এগুলোর একটির কথা বলছেন?",
  exactModelPlaceholder: "যেমন iPhone 13 Pro 256 GB",
  confirmSpecification: "স্পেসিফিকেশন মিলিয়ে নিই। Cashit-এ এই মডেলের জন্য এটাই আছে - আপনার ডিভাইসে যা আলাদা তা বদলে নিন।",
  verifyCopy: "আপনার দাম তৈরি। নিশ্চিত করতে আমরা এককালীন কোড পাঠাব, তারপর আপনার Cashit অ্যাকাউন্টে বিক্রির অনুরোধ তৈরি করব। আপনি যা বলেছেন সব রাখা আছে।",
  registerCopy: "{mobile} Cashit-এ নতুন। শুধু আপনার নাম বলুন - এই একই অ্যাকাউন্ট আপনি cashit.in-এ ব্যবহার করবেন।",
  visitNote: "Cashit আপনার সঙ্গে সময় নিশ্চিত করবে — এতে এজেন্ট জানবে কখন আপনার সুবিধা।",
  doneMessage: "হয়ে গেছে। আপনার বিক্রির অনুরোধ {leadNumber}, এবং পিকআপের জন্য Cashit যোগাযোগ করবে।",
  doneDetail: "আপনার অনুরোধ {leadNumber} Cashit-এর কাছে, {source} থেকে পাঠানো। এরপর একজন এজেন্ট নিযুক্ত হবে ও পিকআপ করবে; তারপর Cashit ডিভাইস পরীক্ষা করে টাকা দেবে।",
}

const ta: Phrases = {
  ...en,
  category: 'நீங்கள் எதை விற்க விரும்புகிறீர்கள்? ஒரு பிரிவைத் தேர்ந்தெடுக்கவும், அல்லது உங்களிடம் உள்ளதைத் தட்டச்சு செய்யவும்.',
  whichOfThese: 'இவற்றில் எது?',
  exactModel: 'சரியான மாடல் எது? பிராண்ட் மற்றும் மாடல் பெயர் போதும்.',
  whichOneDoYouHave: 'உங்களிடம் எது இருக்கிறது?',
  whichConfiguration: 'உங்கள் கட்டமைப்பு எது?',
  howOld: 'சாதனம் எவ்வளவு பழையது?',
  howDoesItLook: 'இது எப்படி இருக்கிறது? பொருந்தும் அனைத்தையும் தேர்ந்தெடுக்கவும்.',
  thatIsItsCondition: 'இதுதான் அதன் நிலை',
  noMarksAtAll: 'எந்த அடையாளமும் இல்லை',
  anythingNotWorking: 'ஏதாவது வேலை செய்யவில்லையா? பொருந்தும் அனைத்தையும் தேர்ந்தெடுக்கவும், அல்லது எல்லாம் சரி என்று சொல்லுங்கள்.',
  thatIsAllThatIsWrong: 'இவ்வளவுதான் பிரச்சினை',
  everythingWorks: 'எல்லாம் வேலை செய்கிறது',
  whichDoYouStillHave: 'இவற்றில் எவை இன்னும் உங்களிடம் உள்ளன?',
  thatIsWhatIHave: 'என்னிடம் இதுதான் இருக்கிறது',
  iHaveNoneOfThese: 'இவற்றில் எதுவும் இல்லை',
  pincode: 'கடைசி கேள்வி: பிக்கப்புக்கான உங்கள் ஆறு இலக்க பின்கோடு என்ன?',
  pincodePlaceholder: '6 இலக்க பின்கோடு',
  quote: "இதற்கு Cashit {amount} ரூபாய் தரும், சோதனைக்குப் பிறகு உறுதி செய்யப்படும். தொடரலாமா?",
  composerPlaceholder: "சாதனத்தின் பெயரை உள்ளிடவும், அல்லது எதையும் கேளுங்கள்…",
  verifyHeading: "உங்கள் மொபைல் எண்ணைச் சரிபார்க்கவும்",
  mobileLabel: "மொபைல் எண்",
  mobilePlaceholder: "10 இலக்க மொபைல் எண்",
  sendCode: "குறியீட்டை அனுப்பு",
  codeHeading: "குறியீட்டை உள்ளிடவும்",
  codeCopy: "{mobile} எண்ணுக்கு குறியீடு அனுப்பப்பட்டது. இது {minutes} நிமிடங்களுக்கு செல்லுபடியாகும்.",
  codeLabel: "ஒருமுறை குறியீடு",
  verifyAndContinue: "சரிபார்த்து தொடரவும்",
  resendIn: "{seconds} வினாடிகளில் மீண்டும் அனுப்பு",
  resendCode: "குறியீட்டை மீண்டும் அனுப்பு",
  changeNumber: "எண்ணை மாற்று",
  registerHeading: "உங்கள் Cashit கணக்கை உருவாக்கவும்",
  fullName: "முழு பெயர்",
  emailOptional: "மின்னஞ்சல் (விருப்பம்)",
  createAccount: "கணக்கை உருவாக்கி தொடரவும்",
  confirmHeading: "உங்கள் விற்பனை கோரிக்கையை உறுதிப்படுத்தவும்",
  confirmCopy: "நீங்கள் உறுதிப்படுத்தும் வரை எதுவும் உருவாக்கப்படாது. பிக்கப்பை ஏற்பாடு செய்ய Cashit தொடர்பு கொள்ளும், இறுதித் தொகை சோதனைக்குப் பிறகு உறுதி செய்யப்படும்.",
  account: "கணக்கு",
  mobile: "மொபைல்",
  device: "சாதனம்",
  cashitPays: "Cashit தரும்",
  placedVia: "விற்பனை வழி",
  pickupAddress: "பிக்கப் முகவரி",
  differentAddress: "வேறு முகவரியிலிருந்து எடுக்கவும்",
  pickupPincode: "பிக்கப் பின்கோடு",
  area: "பகுதி",
  address: "முகவரி",
  landmarkOptional: "அடையாளம் (விருப்பம்)",
  whenCollect: "எப்போது வந்து எடுக்க வேண்டும்?",
  slotMorning: "காலை · 10 – 1",
  slotAfternoon: "மதியம் · 1 – 4",
  slotEvening: "மாலை · 4 – 7",
  today: "இன்று",
  tomorrow: "நாளை",
  confirmButton: "உறுதிப்படுத்தி கோரிக்கையை உருவாக்கவும்",
  trackSale: "இந்த விற்பனையைக் கண்காணிக்கவும்",
  trackSaleHint: "நிலை மற்றும் அடுத்து என்ன என்பதைப் பாருங்கள்",
  didYouMeanOne: "நீங்கள் இதைத்தான் சொல்கிறீர்களா?",
  didYouMeanMany: "நீங்கள் இவற்றில் ஒன்றைச் சொல்கிறீர்களா?",
  exactModelPlaceholder: "எ.கா. iPhone 13 Pro 256 GB",
  confirmSpecification: "விவரக்குறிப்பை உறுதி செய்வோம். இந்த மாடலுக்கு Cashit-இல் இதுதான் உள்ளது - உங்கள் சாதனத்தில் வேறுபட்டதை மாற்றவும்.",
  verifyCopy: "உங்கள் விலை தயார். நீங்கள்தான் என்பதை உறுதிப்படுத்த ஒருமுறை குறியீடு அனுப்புவோம், பிறகு உங்கள் Cashit கணக்கில் விற்பனை கோரிக்கையை உருவாக்குவோம். நீங்கள் சொன்னது அனைத்தும் சேமிக்கப்பட்டுள்ளது.",
  registerCopy: "{mobile} Cashit-க்கு புதியது. உங்கள் பெயர் மட்டும் சொல்லுங்கள் - இதே கணக்கைத்தான் cashit.in-இல் பயன்படுத்துவீர்கள்.",
  visitNote: "Cashit உங்களிடம் வருகையை உறுதி செய்யும் — இது முகவருக்கு உங்களுக்கு வசதியான நேரத்தைத் தெரிவிக்கும்.",
  doneMessage: "முடிந்தது. உங்கள் விற்பனை கோரிக்கை {leadNumber}, பிக்கப்புக்கு Cashit தொடர்பு கொள்ளும்.",
  doneDetail: "உங்கள் கோரிக்கை {leadNumber} Cashit-இடம் உள்ளது, {source} வழியாக அனுப்பப்பட்டது. அடுத்து ஒரு முகவர் நியமிக்கப்பட்டு பிக்கப் செய்வார்; பின்னர் Cashit சாதனத்தைச் சோதித்து பணம் தரும்.",
}

const te: Phrases = {
  ...en,
  category: 'మీరు ఏమి అమ్మాలనుకుంటున్నారు? ఒక విభాగాన్ని ఎంచుకోండి, లేదా మీ దగ్గర ఉన్నది టైప్ చేయండి.',
  whichOfThese: 'వీటిలో ఏది?',
  exactModel: 'ఖచ్చితమైన మోడల్ ఏది? బ్రాండ్ మరియు మోడల్ పేరు చాలు.',
  whichOneDoYouHave: 'మీ దగ్గర ఏది ఉంది?',
  whichConfiguration: 'మీ కాన్ఫిగరేషన్ ఏది?',
  howOld: 'పరికరం ఎంత పాతది?',
  howDoesItLook: 'ఇది ఎలా ఉంది? వర్తించే అన్నిటినీ ఎంచుకోండి.',
  thatIsItsCondition: 'దాని స్థితి ఇదే',
  noMarksAtAll: 'ఎలాంటి గుర్తులు లేవు',
  anythingNotWorking: 'ఏదైనా పని చేయడం లేదా? వర్తించే అన్నీ ఎంచుకోండి, లేదా అంతా బాగుంది అని చెప్పండి.',
  thatIsAllThatIsWrong: 'ఇంతే సమస్య',
  everythingWorks: 'అంతా పని చేస్తుంది',
  whichDoYouStillHave: 'వీటిలో ఏవి ఇంకా మీ దగ్గర ఉన్నాయి?',
  thatIsWhatIHave: 'నా దగ్గర ఇదే ఉంది',
  iHaveNoneOfThese: 'వీటిలో ఏదీ లేదు',
  pincode: 'చివరి ప్రశ్న: పికప్ కోసం మీ ఆరు అంకెల పిన్‌కోడ్ ఏమిటి?',
  pincodePlaceholder: '6 అంకెల పిన్‌కోడ్',
  quote: "దీనికి Cashit {amount} రూపాయలు ఇస్తుంది, తనిఖీ తర్వాత ఖరారు అవుతుంది. ముందుకు వెళ్దామా?",
  composerPlaceholder: "పరికరం పేరు టైప్ చేయండి, లేదా ఏదైనా అడగండి…",
  verifyHeading: "మీ మొబైల్ నంబర్ ధృవీకరించండి",
  mobileLabel: "మొబైల్ నంబర్",
  mobilePlaceholder: "10 అంకెల మొబైల్ నంబర్",
  sendCode: "కోడ్ పంపండి",
  codeHeading: "కోడ్ నమోదు చేయండి",
  codeCopy: "{mobile}కు కోడ్ పంపాము. ఇది {minutes} నిమిషాలు చెల్లుతుంది.",
  codeLabel: "ఒక్కసారి కోడ్",
  verifyAndContinue: "ధృవీకరించి కొనసాగండి",
  resendIn: "{seconds} సెకన్లలో మళ్లీ పంపండి",
  resendCode: "కోడ్ మళ్లీ పంపండి",
  changeNumber: "నంబర్ మార్చండి",
  registerHeading: "మీ Cashit ఖాతా సృష్టించండి",
  fullName: "పూర్తి పేరు",
  emailOptional: "ఇమెయిల్ (ఐచ్ఛికం)",
  createAccount: "ఖాతా సృష్టించి కొనసాగండి",
  confirmHeading: "మీ అమ్మకం అభ్యర్థనను నిర్ధారించండి",
  confirmCopy: "మీరు నిర్ధారించే వరకు ఏదీ సృష్టించబడదు. పికప్ ఏర్పాటు చేయడానికి Cashit సంప్రదిస్తుంది, తుది మొత్తం తనిఖీ తర్వాత ఖరారు అవుతుంది.",
  account: "ఖాతా",
  mobile: "మొబైల్",
  device: "పరికరం",
  cashitPays: "Cashit ఇస్తుంది",
  placedVia: "అమ్మకం మార్గం",
  pickupAddress: "పికప్ చిరునామా",
  differentAddress: "వేరే చిరునామా నుండి తీసుకోండి",
  pickupPincode: "పికప్ పిన్‌కోడ్",
  area: "ప్రాంతం",
  address: "చిరునామా",
  landmarkOptional: "ల్యాండ్‌మార్క్ (ఐచ్ఛికం)",
  whenCollect: "ఎప్పుడు తీసుకోవడానికి రావాలి?",
  slotMorning: "ఉదయం · 10 – 1",
  slotAfternoon: "మధ్యాహ్నం · 1 – 4",
  slotEvening: "సాయంత్రం · 4 – 7",
  today: "ఈరోజు",
  tomorrow: "రేపు",
  confirmButton: "నిర్ధారించి అభ్యర్థన సృష్టించండి",
  trackSale: "ఈ అమ్మకాన్ని ట్రాక్ చేయండి",
  trackSaleHint: "స్థితి మరియు తదుపరి ఏమిటో చూడండి",
  didYouMeanOne: "మీరు దీని గురించేనా చెబుతున్నది?",
  didYouMeanMany: "మీరు వీటిలో ఒకదాని గురించి చెబుతున్నారా?",
  exactModelPlaceholder: "ఉదా. iPhone 13 Pro 256 GB",
  confirmSpecification: "వివరాలు నిర్ధారించుకుందాం. ఈ మోడల్‌కు Cashit వద్ద ఇదే ఉంది - మీ పరికరంలో వేరుగా ఉన్నది మార్చండి.",
  verifyCopy: "మీ ధర సిద్ధంగా ఉంది. మీరేనని నిర్ధారించడానికి ఒక్కసారి కోడ్ పంపుతాము, తర్వాత మీ Cashit ఖాతాలో అమ్మకం అభ్యర్థన సృష్టిస్తాము. మీరు చెప్పినదంతా భద్రంగా ఉంది.",
  registerCopy: "{mobile} Cashit‌కు కొత్తది. మీ పేరు చెప్పండి చాలు - ఇదే ఖాతాను మీరు cashit.in లో వాడతారు.",
  visitNote: "Cashit మీతో సందర్శనను నిర్ధారిస్తుంది — ఇది ఏజెంట్‌కు మీకు అనుకూలమైన సమయం తెలియజేస్తుంది.",
  doneMessage: "అయిపోయింది. మీ అమ్మకం అభ్యర్థన {leadNumber}, పికప్ కోసం Cashit సంప్రదిస్తుంది.",
  doneDetail: "మీ అభ్యర్థన {leadNumber} Cashit వద్ద ఉంది, {source} ద్వారా పంపబడింది. తర్వాత ఏజెంట్ నియమించబడి పికప్ చేస్తారు; ఆ తర్వాత Cashit పరికరాన్ని తనిఖీ చేసి డబ్బు ఇస్తుంది.",
}

const kn: Phrases = {
  ...en,
  category: 'ನೀವು ಏನು ಮಾರಾಟ ಮಾಡಲು ಬಯಸುತ್ತೀರಿ? ಒಂದು ವರ್ಗವನ್ನು ಆಯ್ಕೆಮಾಡಿ, ಅಥವಾ ನಿಮ್ಮ ಬಳಿ ಇರುವುದನ್ನು ಟೈಪ್ ಮಾಡಿ.',
  whichOfThese: 'ಇವುಗಳಲ್ಲಿ ಯಾವುದು?',
  exactModel: 'ನಿಖರವಾದ ಮಾದರಿ ಯಾವುದು? ಬ್ರಾಂಡ್ ಮತ್ತು ಮಾದರಿಯ ಹೆಸರು ಸಾಕು.',
  whichOneDoYouHave: 'ನಿಮ್ಮ ಬಳಿ ಯಾವುದು ಇದೆ?',
  whichConfiguration: 'ನಿಮ್ಮ ಸಂರಚನೆ ಯಾವುದು?',
  howOld: 'ಸಾಧನ ಎಷ್ಟು ಹಳೆಯದು?',
  howDoesItLook: 'ಇದು ಹೇಗೆ ಕಾಣುತ್ತದೆ? ಅನ್ವಯಿಸುವ ಎಲ್ಲವನ್ನೂ ಆಯ್ಕೆಮಾಡಿ.',
  thatIsItsCondition: 'ಇದೇ ಅದರ ಸ್ಥಿತಿ',
  noMarksAtAll: 'ಯಾವುದೇ ಗುರುತುಗಳಿಲ್ಲ',
  anythingNotWorking: 'ಏನಾದರೂ ಕೆಲಸ ಮಾಡುತ್ತಿಲ್ಲವೇ? ಅನ್ವಯಿಸುವ ಎಲ್ಲವನ್ನೂ ಆಯ್ಕೆಮಾಡಿ, ಅಥವಾ ಎಲ್ಲವೂ ಸರಿ ಎಂದು ಹೇಳಿ.',
  thatIsAllThatIsWrong: 'ಇಷ್ಟೇ ಸಮಸ್ಯೆ',
  everythingWorks: 'ಎಲ್ಲವೂ ಕೆಲಸ ಮಾಡುತ್ತದೆ',
  whichDoYouStillHave: 'ಇವುಗಳಲ್ಲಿ ಯಾವುದು ಇನ್ನೂ ನಿಮ್ಮ ಬಳಿ ಇದೆ?',
  thatIsWhatIHave: 'ನನ್ನ ಬಳಿ ಇದೇ ಇದೆ',
  iHaveNoneOfThese: 'ಇವುಗಳಲ್ಲಿ ಯಾವುದೂ ಇಲ್ಲ',
  pincode: 'ಕೊನೆಯ ಪ್ರಶ್ನೆ: ಪಿಕಪ್‌ಗಾಗಿ ನಿಮ್ಮ ಆರು ಅಂಕಿಯ ಪಿನ್‌ಕೋಡ್ ಯಾವುದು?',
  pincodePlaceholder: '6 ಅಂಕಿಯ ಪಿನ್‌ಕೋಡ್',
  quote: "ಇದಕ್ಕೆ Cashit {amount} ರೂಪಾಯಿ ನೀಡುತ್ತದೆ, ಪರಿಶೀಲನೆಯ ನಂತರ ಖಚಿತವಾಗುತ್ತದೆ. ಮುಂದುವರಿಯೋಣವೇ?",
  composerPlaceholder: "ಸಾಧನದ ಹೆಸರು ಟೈಪ್ ಮಾಡಿ, ಅಥವಾ ಏನಾದರೂ ಕೇಳಿ…",
  verifyHeading: "ನಿಮ್ಮ ಮೊಬೈಲ್ ಸಂಖ್ಯೆ ಪರಿಶೀಲಿಸಿ",
  mobileLabel: "ಮೊಬೈಲ್ ಸಂಖ್ಯೆ",
  mobilePlaceholder: "10 ಅಂಕಿಯ ಮೊಬೈಲ್ ಸಂಖ್ಯೆ",
  sendCode: "ಕೋಡ್ ಕಳುಹಿಸಿ",
  codeHeading: "ಕೋಡ್ ನಮೂದಿಸಿ",
  codeCopy: "{mobile} ಗೆ ಕೋಡ್ ಕಳುಹಿಸಿದ್ದೇವೆ. ಇದು {minutes} ನಿಮಿಷ ಮಾನ್ಯ.",
  codeLabel: "ಒಂದು ಬಾರಿಯ ಕೋಡ್",
  verifyAndContinue: "ಪರಿಶೀಲಿಸಿ ಮುಂದುವರಿಯಿರಿ",
  resendIn: "{seconds} ಸೆಕೆಂಡಿನಲ್ಲಿ ಮತ್ತೆ ಕಳುಹಿಸಿ",
  resendCode: "ಕೋಡ್ ಮತ್ತೆ ಕಳುಹಿಸಿ",
  changeNumber: "ಸಂಖ್ಯೆ ಬದಲಿಸಿ",
  registerHeading: "ನಿಮ್ಮ Cashit ಖಾತೆ ರಚಿಸಿ",
  fullName: "ಪೂರ್ಣ ಹೆಸರು",
  emailOptional: "ಇಮೇಲ್ (ಐಚ್ಛಿಕ)",
  createAccount: "ಖಾತೆ ರಚಿಸಿ ಮುಂದುವರಿಯಿರಿ",
  confirmHeading: "ನಿಮ್ಮ ಮಾರಾಟದ ವಿನಂತಿಯನ್ನು ಖಚಿತಪಡಿಸಿ",
  confirmCopy: "ನೀವು ಖಚಿತಪಡಿಸುವವರೆಗೆ ಏನೂ ರಚನೆಯಾಗುವುದಿಲ್ಲ. ಪಿಕಪ್ ವ್ಯವಸ್ಥೆಗೆ Cashit ಸಂಪರ್ಕಿಸುತ್ತದೆ, ಅಂತಿಮ ಮೊತ್ತ ಪರಿಶೀಲನೆಯ ನಂತರ ಖಚಿತವಾಗುತ್ತದೆ.",
  account: "ಖಾತೆ",
  mobile: "ಮೊಬೈಲ್",
  device: "ಸಾಧನ",
  cashitPays: "Cashit ನೀಡುತ್ತದೆ",
  placedVia: "ಮಾರಾಟದ ಮಾರ್ಗ",
  pickupAddress: "ಪಿಕಪ್ ವಿಳಾಸ",
  differentAddress: "ಬೇರೆ ವಿಳಾಸದಿಂದ ಪಡೆಯಿರಿ",
  pickupPincode: "ಪಿಕಪ್ ಪಿನ್‌ಕೋಡ್",
  area: "ಪ್ರದೇಶ",
  address: "ವಿಳಾಸ",
  landmarkOptional: "ಲ್ಯಾಂಡ್‌ಮಾರ್ಕ್ (ಐಚ್ಛಿಕ)",
  whenCollect: "ಯಾವಾಗ ಬಂದು ಪಡೆಯಬೇಕು?",
  slotMorning: "ಬೆಳಿಗ್ಗೆ · 10 – 1",
  slotAfternoon: "ಮಧ್ಯಾಹ್ನ · 1 – 4",
  slotEvening: "ಸಂಜೆ · 4 – 7",
  today: "ಇಂದು",
  tomorrow: "ನಾಳೆ",
  confirmButton: "ಖಚಿತಪಡಿಸಿ ವಿನಂತಿ ರಚಿಸಿ",
  trackSale: "ಈ ಮಾರಾಟವನ್ನು ಟ್ರ್ಯಾಕ್ ಮಾಡಿ",
  trackSaleHint: "ಸ್ಥಿತಿ ಮತ್ತು ಮುಂದೇನು ಎಂದು ನೋಡಿ",
  didYouMeanOne: "ನೀವು ಇದನ್ನೇ ಹೇಳುತ್ತಿದ್ದೀರಾ?",
  didYouMeanMany: "ನೀವು ಇವುಗಳಲ್ಲಿ ಒಂದನ್ನು ಹೇಳುತ್ತಿದ್ದೀರಾ?",
  exactModelPlaceholder: "ಉದಾ. iPhone 13 Pro 256 GB",
  confirmSpecification: "ವಿವರಗಳನ್ನು ಖಚಿತಪಡಿಸೋಣ. ಈ ಮಾದರಿಗೆ Cashit ನಲ್ಲಿ ಇದೇ ಇದೆ - ನಿಮ್ಮ ಸಾಧನದಲ್ಲಿ ಬೇರೆ ಇರುವುದನ್ನು ಬದಲಾಯಿಸಿ.",
  verifyCopy: "ನಿಮ್ಮ ಬೆಲೆ ಸಿದ್ಧವಾಗಿದೆ. ನೀವೇ ಎಂದು ಖಚಿತಪಡಿಸಲು ಒಂದು ಬಾರಿಯ ಕೋಡ್ ಕಳುಹಿಸುತ್ತೇವೆ, ನಂತರ ನಿಮ್ಮ Cashit ಖಾತೆಯಲ್ಲಿ ಮಾರಾಟದ ವಿನಂತಿ ರಚಿಸುತ್ತೇವೆ. ನೀವು ಹೇಳಿದ್ದೆಲ್ಲ ಉಳಿಸಲಾಗಿದೆ.",
  registerCopy: "{mobile} Cashit ಗೆ ಹೊಸದು. ನಿಮ್ಮ ಹೆಸರು ಹೇಳಿದರೆ ಸಾಕು - ಇದೇ ಖಾತೆಯನ್ನು ನೀವು cashit.in ನಲ್ಲಿ ಬಳಸುತ್ತೀರಿ.",
  visitNote: "Cashit ನಿಮ್ಮೊಂದಿಗೆ ಭೇಟಿಯನ್ನು ಖಚಿತಪಡಿಸುತ್ತದೆ — ಇದು ಏಜೆಂಟ್‌ಗೆ ನಿಮಗೆ ಅನುಕೂಲದ ಸಮಯ ತಿಳಿಸುತ್ತದೆ.",
  doneMessage: "ಆಯಿತು. ನಿಮ್ಮ ಮಾರಾಟದ ವಿನಂತಿ {leadNumber}, ಪಿಕಪ್‌ಗಾಗಿ Cashit ಸಂಪರ್ಕಿಸುತ್ತದೆ.",
  doneDetail: "ನಿಮ್ಮ ವಿನಂತಿ {leadNumber} Cashit ಬಳಿ ಇದೆ, {source} ಮೂಲಕ ಕಳುಹಿಸಲಾಗಿದೆ. ಮುಂದೆ ಏಜೆಂಟ್ ನೇಮಕವಾಗಿ ಪಿಕಪ್ ಮಾಡುತ್ತಾರೆ; ನಂತರ Cashit ಸಾಧನ ಪರಿಶೀಲಿಸಿ ಹಣ ನೀಡುತ್ತದೆ.",
}

const gu: Phrases = {
  ...en,
  category: 'તમે શું વેચવા માંગો છો? એક શ્રેણી પસંદ કરો, અથવા તમારી પાસે જે છે તે લખો.',
  whichOfThese: 'આમાંથી કયું છે?',
  exactModel: 'ચોક્કસ મોડેલ કયું છે? બ્રાન્ડ અને મોડેલનું નામ પૂરતું છે.',
  whichOneDoYouHave: 'તમારી પાસે કયું છે?',
  whichConfiguration: 'તમારું કન્ફિગરેશન કયું છે?',
  howOld: 'ઉપકરણ કેટલું જૂનું છે?',
  howDoesItLook: 'તે દેખાવમાં કેવું છે? લાગુ પડતું બધું પસંદ કરો.',
  thatIsItsCondition: 'તેની હાલત આ જ છે',
  noMarksAtAll: 'કોઈ નિશાન નથી',
  anythingNotWorking: 'કંઈ કામ નથી કરતું? લાગુ પડતું બધું પસંદ કરો, અથવા કહો કે બધું બરાબર છે.',
  thatIsAllThatIsWrong: 'આટલી જ ખામી છે',
  everythingWorks: 'બધું કામ કરે છે',
  whichDoYouStillHave: 'આમાંથી શું હજુ તમારી પાસે છે?',
  thatIsWhatIHave: 'મારી પાસે આ છે',
  iHaveNoneOfThese: 'આમાંથી કંઈ નથી',
  pincode: 'છેલ્લો પ્રશ્ન: પિકઅપ માટે તમારો છ અંકનો પિનકોડ શું છે?',
  pincodePlaceholder: '6 અંકનો પિનકોડ',
  quote: "આના માટે Cashit {amount} રૂપિયા આપશે, તપાસ પછી નક્કી થશે. આગળ વધીએ?",
  composerPlaceholder: "ઉપકરણનું નામ લખો, અથવા કંઈ પણ પૂછો…",
  verifyHeading: "તમારો મોબાઇલ નંબર ચકાસો",
  mobileLabel: "મોબાઇલ નંબર",
  mobilePlaceholder: "10 અંકનો મોબાઇલ નંબર",
  sendCode: "કોડ મોકલો",
  codeHeading: "કોડ દાખલ કરો",
  codeCopy: "અમે {mobile} પર કોડ મોકલ્યો છે. તે {minutes} મિનિટ માન્ય છે.",
  codeLabel: "એક વખતનો કોડ",
  verifyAndContinue: "ચકાસો અને આગળ વધો",
  resendIn: "{seconds} સેકન્ડમાં ફરી મોકલો",
  resendCode: "કોડ ફરી મોકલો",
  changeNumber: "નંબર બદલો",
  registerHeading: "તમારું Cashit ખાતું બનાવો",
  fullName: "પૂરું નામ",
  emailOptional: "ઈમેલ (વૈકલ્પિક)",
  createAccount: "ખાતું બનાવો અને આગળ વધો",
  confirmHeading: "તમારી વેચાણ વિનંતી ખાતરી કરો",
  confirmCopy: "તમે ખાતરી ન કરો ત્યાં સુધી કંઈ બનતું નથી. પિકઅપ ગોઠવવા Cashit સંપર્ક કરશે, અને અંતિમ રકમ તપાસ પછી નક્કી થશે.",
  account: "ખાતું",
  mobile: "મોબાઇલ",
  device: "ઉપકરણ",
  cashitPays: "Cashit આપશે",
  placedVia: "વેચાણનું માધ્યમ",
  pickupAddress: "પિકઅપનું સરનામું",
  differentAddress: "બીજા સરનામેથી લેવું છે",
  pickupPincode: "પિકઅપ પિનકોડ",
  area: "વિસ્તાર",
  address: "સરનામું",
  landmarkOptional: "લેન્ડમાર્ક (વૈકલ્પિક)",
  whenCollect: "અમે ક્યારે લેવા આવીએ?",
  slotMorning: "સવારે · 10 – 1",
  slotAfternoon: "બપોરે · 1 – 4",
  slotEvening: "સાંજે · 4 – 7",
  today: "આજે",
  tomorrow: "આવતીકાલે",
  confirmButton: "ખાતરી કરો અને વિનંતી બનાવો",
  trackSale: "આ વેચાણ ટ્રૅક કરો",
  trackSaleHint: "સ્થિતિ અને આગળ શું થશે જુઓ",
  didYouMeanOne: "તમે આની જ વાત કરો છો?",
  didYouMeanMany: "તમે આમાંથી કોઈ એકની વાત કરો છો?",
  exactModelPlaceholder: "દા.ત. iPhone 13 Pro 256 GB",
  confirmSpecification: "વિગતો ચકાસી લઈએ. આ મોડેલ માટે Cashit પાસે આ જ નોંધાયેલું છે - તમારા ઉપકરણમાં જે અલગ હોય તે બદલો.",
  verifyCopy: "તમારી કિંમત તૈયાર છે. ખાતરી માટે અમે એક વખતનો કોડ મોકલીશું, પછી તમારા Cashit ખાતામાં વેચાણ વિનંતી બનાવીશું. તમે જે કહ્યું છે તે બધું સાચવેલું છે.",
  registerCopy: "{mobile} Cashit પર નવું છે. ફક્ત તમારું નામ જણાવો - આ જ ખાતું તમે cashit.in પર વાપરશો.",
  visitNote: "Cashit તમારી સાથે મુલાકાત નક્કી કરશે — આથી એજન્ટને તમારી અનુકૂળ સમય ખબર પડે છે.",
  doneMessage: "થઈ ગયું. તમારી વેચાણ વિનંતી {leadNumber} છે, અને પિકઅપ માટે Cashit સંપર્ક કરશે.",
  doneDetail: "તમારી વિનંતી {leadNumber} Cashit પાસે છે, {source} થી મોકલાઈ. પછી એજન્ટ નિમાશે અને પિકઅપ કરશે; ત્યારબાદ Cashit ઉપકરણ તપાસી તમને ચૂકવણી કરશે.",
}

const pa: Phrases = {
  ...en,
  category: 'ਤੁਸੀਂ ਕੀ ਵੇਚਣਾ ਚਾਹੁੰਦੇ ਹੋ? ਇੱਕ ਸ਼੍ਰੇਣੀ ਚੁਣੋ, ਜਾਂ ਜੋ ਤੁਹਾਡੇ ਕੋਲ ਹੈ ਉਹ ਲਿਖੋ।',
  whichOfThese: 'ਇਹਨਾਂ ਵਿੱਚੋਂ ਕਿਹੜਾ ਹੈ?',
  exactModel: 'ਸਹੀ ਮਾਡਲ ਕਿਹੜਾ ਹੈ? ਬ੍ਰਾਂਡ ਅਤੇ ਮਾਡਲ ਦਾ ਨਾਮ ਕਾਫ਼ੀ ਹੈ।',
  whichOneDoYouHave: 'ਤੁਹਾਡੇ ਕੋਲ ਕਿਹੜਾ ਹੈ?',
  whichConfiguration: 'ਤੁਹਾਡੀ ਕਨਫ਼ਿਗਰੇਸ਼ਨ ਕਿਹੜੀ ਹੈ?',
  howOld: 'ਡਿਵਾਈਸ ਕਿੰਨਾ ਪੁਰਾਣਾ ਹੈ?',
  howDoesItLook: 'ਇਹ ਦਿੱਖ ਵਿੱਚ ਕਿਹੋ ਜਿਹਾ ਹੈ? ਲਾਗੂ ਹੋਣ ਵਾਲਾ ਸਭ ਕੁਝ ਚੁਣੋ।',
  thatIsItsCondition: 'ਇਸਦੀ ਹਾਲਤ ਇਹੀ ਹੈ',
  noMarksAtAll: 'ਕੋਈ ਨਿਸ਼ਾਨ ਨਹੀਂ',
  anythingNotWorking: 'ਕੀ ਕੁਝ ਕੰਮ ਨਹੀਂ ਕਰ ਰਿਹਾ? ਲਾਗੂ ਹੋਣ ਵਾਲਾ ਸਭ ਚੁਣੋ, ਜਾਂ ਦੱਸੋ ਕਿ ਸਭ ਠੀਕ ਹੈ।',
  thatIsAllThatIsWrong: 'ਬੱਸ ਇੰਨੀ ਹੀ ਖ਼ਰਾਬੀ ਹੈ',
  everythingWorks: 'ਸਭ ਕੁਝ ਕੰਮ ਕਰਦਾ ਹੈ',
  whichDoYouStillHave: 'ਇਹਨਾਂ ਵਿੱਚੋਂ ਕੀ ਅਜੇ ਵੀ ਤੁਹਾਡੇ ਕੋਲ ਹੈ?',
  thatIsWhatIHave: 'ਮੇਰੇ ਕੋਲ ਇਹੀ ਹੈ',
  iHaveNoneOfThese: 'ਇਹਨਾਂ ਵਿੱਚੋਂ ਕੁਝ ਨਹੀਂ',
  pincode: 'ਆਖਰੀ ਸਵਾਲ: ਪਿਕਅੱਪ ਲਈ ਤੁਹਾਡਾ ਛੇ ਅੰਕਾਂ ਦਾ ਪਿਨਕੋਡ ਕੀ ਹੈ?',
  pincodePlaceholder: '6 ਅੰਕਾਂ ਦਾ ਪਿਨਕੋਡ',
  quote: "ਇਸ ਲਈ Cashit {amount} ਰੁਪਏ ਦੇਵੇਗਾ, ਜਾਂਚ ਤੋਂ ਬਾਅਦ ਪੱਕਾ ਹੋਵੇਗਾ। ਕੀ ਅਸੀਂ ਅੱਗੇ ਵਧੀਏ?",
  composerPlaceholder: "ਡਿਵਾਈਸ ਦਾ ਨਾਮ ਲਿਖੋ, ਜਾਂ ਕੁਝ ਵੀ ਪੁੱਛੋ…",
  verifyHeading: "ਆਪਣਾ ਮੋਬਾਈਲ ਨੰਬਰ ਤਸਦੀਕ ਕਰੋ",
  mobileLabel: "ਮੋਬਾਈਲ ਨੰਬਰ",
  mobilePlaceholder: "10 ਅੰਕਾਂ ਦਾ ਮੋਬਾਈਲ ਨੰਬਰ",
  sendCode: "ਕੋਡ ਭੇਜੋ",
  codeHeading: "ਕੋਡ ਦਰਜ ਕਰੋ",
  codeCopy: "ਅਸੀਂ {mobile} ਤੇ ਕੋਡ ਭੇਜਿਆ ਹੈ। ਇਹ {minutes} ਮਿੰਟ ਵੈਧ ਹੈ।",
  codeLabel: "ਇੱਕ ਵਾਰ ਦਾ ਕੋਡ",
  verifyAndContinue: "ਤਸਦੀਕ ਕਰੋ ਅਤੇ ਅੱਗੇ ਵਧੋ",
  resendIn: "{seconds} ਸਕਿੰਟ ਵਿੱਚ ਦੁਬਾਰਾ ਭੇਜੋ",
  resendCode: "ਕੋਡ ਦੁਬਾਰਾ ਭੇਜੋ",
  changeNumber: "ਨੰਬਰ ਬਦਲੋ",
  registerHeading: "ਆਪਣਾ Cashit ਖਾਤਾ ਬਣਾਓ",
  fullName: "ਪੂਰਾ ਨਾਮ",
  emailOptional: "ਈਮੇਲ (ਵਿਕਲਪਿਕ)",
  createAccount: "ਖਾਤਾ ਬਣਾਓ ਅਤੇ ਅੱਗੇ ਵਧੋ",
  confirmHeading: "ਆਪਣੀ ਵਿਕਰੀ ਦੀ ਬੇਨਤੀ ਦੀ ਪੁਸ਼ਟੀ ਕਰੋ",
  confirmCopy: "ਜਦੋਂ ਤੱਕ ਤੁਸੀਂ ਪੁਸ਼ਟੀ ਨਹੀਂ ਕਰਦੇ, ਕੁਝ ਨਹੀਂ ਬਣਦਾ। ਪਿਕਅੱਪ ਲਈ Cashit ਸੰਪਰਕ ਕਰੇਗਾ, ਅਤੇ ਅੰਤਿਮ ਰਕਮ ਜਾਂਚ ਤੋਂ ਬਾਅਦ ਤੈਅ ਹੋਵੇਗੀ।",
  account: "ਖਾਤਾ",
  mobile: "ਮੋਬਾਈਲ",
  device: "ਡਿਵਾਈਸ",
  cashitPays: "Cashit ਦੇਵੇਗਾ",
  placedVia: "ਵਿਕਰੀ ਦਾ ਜ਼ਰੀਆ",
  pickupAddress: "ਪਿਕਅੱਪ ਦਾ ਪਤਾ",
  differentAddress: "ਕਿਸੇ ਹੋਰ ਪਤੇ ਤੋਂ ਲੈਣਾ ਹੈ",
  pickupPincode: "ਪਿਕਅੱਪ ਪਿਨਕੋਡ",
  area: "ਇਲਾਕਾ",
  address: "ਪਤਾ",
  landmarkOptional: "ਲੈਂਡਮਾਰਕ (ਵਿਕਲਪਿਕ)",
  whenCollect: "ਅਸੀਂ ਕਦੋਂ ਲੈਣ ਆਈਏ?",
  slotMorning: "ਸਵੇਰੇ · 10 – 1",
  slotAfternoon: "ਦੁਪਹਿਰੇ · 1 – 4",
  slotEvening: "ਸ਼ਾਮ · 4 – 7",
  today: "ਅੱਜ",
  tomorrow: "ਕੱਲ੍ਹ",
  confirmButton: "ਪੁਸ਼ਟੀ ਕਰੋ ਅਤੇ ਬੇਨਤੀ ਬਣਾਓ",
  trackSale: "ਇਸ ਵਿਕਰੀ ਨੂੰ ਟਰੈਕ ਕਰੋ",
  trackSaleHint: "ਹਾਲਤ ਅਤੇ ਅੱਗੇ ਕੀ ਹੋਵੇਗਾ ਵੇਖੋ",
  didYouMeanOne: "ਕੀ ਤੁਸੀਂ ਇਸੇ ਦੀ ਗੱਲ ਕਰ ਰਹੇ ਹੋ?",
  didYouMeanMany: "ਕੀ ਤੁਸੀਂ ਇਹਨਾਂ ਵਿੱਚੋਂ ਕਿਸੇ ਦੀ ਗੱਲ ਕਰ ਰਹੇ ਹੋ?",
  exactModelPlaceholder: "ਜਿਵੇਂ iPhone 13 Pro 256 GB",
  confirmSpecification: "ਵੇਰਵੇ ਪੱਕੇ ਕਰ ਲਈਏ। ਇਸ ਮਾਡਲ ਲਈ Cashit ਕੋਲ ਇਹੀ ਦਰਜ ਹੈ - ਤੁਹਾਡੇ ਡਿਵਾਈਸ ਵਿੱਚ ਜੋ ਵੱਖਰਾ ਹੋਵੇ ਉਹ ਬਦਲੋ।",
  verifyCopy: "ਤੁਹਾਡੀ ਕੀਮਤ ਤਿਆਰ ਹੈ। ਪੁਸ਼ਟੀ ਲਈ ਅਸੀਂ ਇੱਕ ਵਾਰ ਦਾ ਕੋਡ ਭੇਜਾਂਗੇ, ਫਿਰ ਤੁਹਾਡੇ Cashit ਖਾਤੇ ਵਿੱਚ ਵਿਕਰੀ ਦੀ ਬੇਨਤੀ ਬਣਾਵਾਂਗੇ। ਤੁਸੀਂ ਜੋ ਦੱਸਿਆ ਹੈ ਸਭ ਸੰਭਾਲਿਆ ਹੋਇਆ ਹੈ।",
  registerCopy: "{mobile} Cashit ਤੇ ਨਵਾਂ ਹੈ। ਬੱਸ ਆਪਣਾ ਨਾਮ ਦੱਸੋ - ਇਹੀ ਖਾਤਾ ਤੁਸੀਂ cashit.in ਤੇ ਵਰਤੋਗੇ।",
  visitNote: "Cashit ਤੁਹਾਡੇ ਨਾਲ ਮੁਲਾਕਾਤ ਪੱਕੀ ਕਰੇਗਾ — ਇਸ ਨਾਲ ਏਜੰਟ ਨੂੰ ਤੁਹਾਡਾ ਸੁਵਿਧਾਜਨਕ ਸਮਾਂ ਪਤਾ ਲੱਗਦਾ ਹੈ।",
  doneMessage: "ਹੋ ਗਿਆ। ਤੁਹਾਡੀ ਵਿਕਰੀ ਦੀ ਬੇਨਤੀ {leadNumber} ਹੈ, ਅਤੇ ਪਿਕਅੱਪ ਲਈ Cashit ਸੰਪਰਕ ਕਰੇਗਾ।",
  doneDetail: "ਤੁਹਾਡੀ ਬੇਨਤੀ {leadNumber} Cashit ਕੋਲ ਹੈ, {source} ਤੋਂ ਭੇਜੀ ਗਈ। ਅੱਗੇ ਇੱਕ ਏਜੰਟ ਨਿਯੁਕਤ ਹੋਵੇਗਾ ਅਤੇ ਪਿਕਅੱਪ ਕਰੇਗਾ; ਉਸ ਤੋਂ ਬਾਅਦ Cashit ਡਿਵਾਈਸ ਜਾਂਚ ਕੇ ਭੁਗਤਾਨ ਕਰੇਗਾ।",
}

const ml: Phrases = {
  ...en,
  category: 'നിങ്ങൾ എന്താണ് വിൽക്കാൻ ആഗ്രഹിക്കുന്നത്? ഒരു വിഭാഗം തിരഞ്ഞെടുക്കുക, അല്ലെങ്കിൽ നിങ്ങളുടെ പക്കലുള്ളത് ടൈപ്പ് ചെയ്യുക.',
  whichOfThese: 'ഇവയിൽ ഏതാണ്?',
  exactModel: 'കൃത്യമായ മോഡൽ ഏതാണ്? ബ്രാൻഡും മോഡലിന്റെ പേരും മതി.',
  whichOneDoYouHave: 'നിങ്ങളുടെ പക്കൽ ഏതാണുള്ളത്?',
  whichConfiguration: 'നിങ്ങളുടെ കോൺഫിഗറേഷൻ ഏതാണ്?',
  howOld: 'ഉപകരണത്തിന് എത്ര പഴക്കമുണ്ട്?',
  howDoesItLook: 'ഇത് കാണാൻ എങ്ങനെയുണ്ട്? ബാധകമായതെല്ലാം തിരഞ്ഞെടുക്കുക.',
  thatIsItsCondition: 'അതിന്റെ അവസ്ഥ ഇതാണ്',
  noMarksAtAll: 'ഒരു പാടും ഇല്ല',
  anythingNotWorking: 'എന്തെങ്കിലും പ്രവർത്തിക്കുന്നില്ലേ? ബാധകമായതെല്ലാം തിരഞ്ഞെടുക്കുക, അല്ലെങ്കിൽ എല്ലാം ശരിയാണെന്ന് പറയുക.',
  thatIsAllThatIsWrong: 'ഇത്രയേ കുഴപ്പമുള്ളൂ',
  everythingWorks: 'എല്ലാം പ്രവർത്തിക്കുന്നു',
  whichDoYouStillHave: 'ഇവയിൽ എന്തെല്ലാം ഇപ്പോഴും നിങ്ങളുടെ പക്കലുണ്ട്?',
  thatIsWhatIHave: 'എന്റെ പക്കൽ ഇതാണുള്ളത്',
  iHaveNoneOfThese: 'ഇവയിൽ ഒന്നുമില്ല',
  pincode: 'അവസാന ചോദ്യം: പിക്കപ്പിനുള്ള നിങ്ങളുടെ ആറക്ക പിൻകോഡ് എന്താണ്?',
  pincodePlaceholder: '6 അക്ക പിൻകോഡ്',
  quote: "ഇതിന് Cashit {amount} രൂപ നൽകും, പരിശോധനയ്ക്ക് ശേഷം ഉറപ്പാക്കും. നമുക്ക് തുടരാമോ?",
  composerPlaceholder: "ഉപകരണത്തിന്റെ പേര് ടൈപ്പ് ചെയ്യുക, അല്ലെങ്കിൽ എന്തും ചോദിക്കൂ…",
  verifyHeading: "നിങ്ങളുടെ മൊബൈൽ നമ്പർ പരിശോധിക്കുക",
  mobileLabel: "മൊബൈൽ നമ്പർ",
  mobilePlaceholder: "10 അക്ക മൊബൈൽ നമ്പർ",
  sendCode: "കോഡ് അയയ്ക്കുക",
  codeHeading: "കോഡ് നൽകുക",
  codeCopy: "{mobile} ലേക്ക് കോഡ് അയച്ചു. ഇത് {minutes} മിനിറ്റ് സാധുവാണ്.",
  codeLabel: "ഒറ്റത്തവണ കോഡ്",
  verifyAndContinue: "പരിശോധിച്ച് തുടരുക",
  resendIn: "{seconds} സെക്കൻഡിൽ വീണ്ടും അയയ്ക്കുക",
  resendCode: "കോഡ് വീണ്ടും അയയ്ക്കുക",
  changeNumber: "നമ്പർ മാറ്റുക",
  registerHeading: "നിങ്ങളുടെ Cashit അക്കൗണ്ട് ഉണ്ടാക്കുക",
  fullName: "പൂർണ്ണ നാമം",
  emailOptional: "ഇമെയിൽ (ഐച്ഛികം)",
  createAccount: "അക്കൗണ്ട് ഉണ്ടാക്കി തുടരുക",
  confirmHeading: "നിങ്ങളുടെ വിൽപ്പന അഭ്യർത്ഥന സ്ഥിരീകരിക്കുക",
  confirmCopy: "നിങ്ങൾ സ്ഥിരീകരിക്കുന്നതുവരെ ഒന്നും ഉണ്ടാക്കില്ല. പിക്കപ്പ് ക്രമീകരിക്കാൻ Cashit ബന്ധപ്പെടും, അന്തിമ തുക പരിശോധനയ്ക്ക് ശേഷം ഉറപ്പാക്കും.",
  account: "അക്കൗണ്ട്",
  mobile: "മൊബൈൽ",
  device: "ഉപകരണം",
  cashitPays: "Cashit നൽകും",
  placedVia: "വിൽപ്പന മാർഗം",
  pickupAddress: "പിക്കപ്പ് വിലാസം",
  differentAddress: "മറ്റൊരു വിലാസത്തിൽ നിന്ന് എടുക്കുക",
  pickupPincode: "പിക്കപ്പ് പിൻകോഡ്",
  area: "പ്രദേശം",
  address: "വിലാസം",
  landmarkOptional: "ലാൻഡ്‌മാർക്ക് (ഐച്ഛികം)",
  whenCollect: "എപ്പോൾ എടുക്കാൻ വരണം?",
  slotMorning: "രാവിലെ · 10 – 1",
  slotAfternoon: "ഉച്ചയ്ക്ക് · 1 – 4",
  slotEvening: "വൈകിട്ട് · 4 – 7",
  today: "ഇന്ന്",
  tomorrow: "നാളെ",
  confirmButton: "സ്ഥിരീകരിച്ച് അഭ്യർത്ഥന ഉണ്ടാക്കുക",
  trackSale: "ഈ വിൽപ്പന ട്രാക്ക് ചെയ്യുക",
  trackSaleHint: "നില, അടുത്തത് എന്ത് എന്ന് കാണുക",
  didYouMeanOne: "നിങ്ങൾ ഇതിനെക്കുറിച്ചാണോ പറയുന്നത്?",
  didYouMeanMany: "നിങ്ങൾ ഇവയിൽ ഒന്നിനെക്കുറിച്ചാണോ പറയുന്നത്?",
  exactModelPlaceholder: "ഉദാ. iPhone 13 Pro 256 GB",
  confirmSpecification: "വിശദാംശങ്ങൾ ഉറപ്പാക്കാം. ഈ മോഡലിന് Cashit-ൽ ഇതാണ് ഉള്ളത് - നിങ്ങളുടെ ഉപകരണത്തിൽ വ്യത്യാസമുള്ളത് മാറ്റുക.",
  verifyCopy: "നിങ്ങളുടെ വില തയ്യാറാണ്. ഉറപ്പാക്കാൻ ഒറ്റത്തവണ കോഡ് അയയ്ക്കും, പിന്നെ നിങ്ങളുടെ Cashit അക്കൗണ്ടിൽ വിൽപ്പന അഭ്യർത്ഥന ഉണ്ടാക്കും. നിങ്ങൾ പറഞ്ഞതെല്ലാം സൂക്ഷിച്ചിട്ടുണ്ട്.",
  registerCopy: "{mobile} Cashit-ന് പുതിയതാണ്. നിങ്ങളുടെ പേര് മാത്രം പറയൂ - ഇതേ അക്കൗണ്ട് തന്നെ cashit.in-ലും ഉപയോഗിക്കും.",
  visitNote: "Cashit നിങ്ങളുമായി സന്ദർശനം ഉറപ്പാക്കും — ഇത് ഏജന്റിന് നിങ്ങൾക്ക് സൗകര്യമുള്ള സമയം അറിയിക്കുന്നു.",
  doneMessage: "കഴിഞ്ഞു. നിങ്ങളുടെ വിൽപ്പന അഭ്യർത്ഥന {leadNumber} ആണ്, പിക്കപ്പിനായി Cashit ബന്ധപ്പെടും.",
  doneDetail: "നിങ്ങളുടെ അഭ്യർത്ഥന {leadNumber} Cashit-ന്റെ പക്കലുണ്ട്, {source} വഴി അയച്ചത്. അടുത്തതായി ഒരു ഏജന്റ് നിയമിക്കപ്പെട്ട് പിക്കപ്പ് ചെയ്യും; പിന്നെ Cashit ഉപകരണം പരിശോധിച്ച് പണം നൽകും.",
}

/**
 * Odia. Its speech is NOT transcribable - neither transcription model lists Odia - so the microphone
 * language menu does not offer it. Typed Odia is another matter: the script is unambiguous, so a customer
 * who writes in it is read correctly and answered in it.
 */
const or: Phrases = {
  category: "ଆପଣ କଣ ବିକ୍ରି କରିବାକୁ ଚାହାଁନ୍ତି? ଏକ ବର୍ଗ ବାଛନ୍ତୁ, କିମ୍ବା ଆପଣଙ୍କ ପାଖରେ ଯାହା ଅଛି ଲେଖନ୍ତୁ।",
  didYouMeanOne: "ଆପଣ ଏହି କଥା କହୁଛନ୍ତି କି?",
  didYouMeanMany: "ଆପଣ ଏଥିମଧ୍ୟରୁ ଗୋଟିଏ କଥା କହୁଛନ୍ତି କି?",
  whichOfThese: "ଏଥିମଧ୍ୟରୁ କେଉଁଟି?",
  exactModel: "ଠିକ୍ କେଉଁ ମଡେଲ୍? ବ୍ରାଣ୍ଡ ଓ ମଡେଲ୍ ନାମ ଯଥେଷ୍ଟ।",
  exactModelPlaceholder: "ଯେପରି iPhone 13 Pro 256 GB",
  whichOneDoYouHave: "ଆପଣଙ୍କ ପାଖରେ କେଉଁଟି ଅଛି?",
  whichConfiguration: "ଆପଣଙ୍କ କନଫିଗରେସନ୍ କେଉଁଟି?",
  confirmSpecification: "ବିବରଣୀ ନିଶ୍ଚିତ କରିନେବା। ଏହି ମଡେଲ୍ ପାଇଁ Cashit ପାଖରେ ଏହା ଅଛି - ଆପଣଙ୍କ ଉପକରଣରେ ଯାହା ଅଲଗା, ତାହା ବଦଳାନ୍ତୁ।",
  howOld: "ଉପକରଣ କେତେ ପୁରୁଣା?",
  howDoesItLook: "ଏହା ଦେଖିବାକୁ କେମିତି? ପ୍ରଯୁଜ୍ୟ ସବୁ ବାଛନ୍ତୁ।",
  thatIsItsCondition: "ଏହାର ଅବସ୍ଥା ଏହିପରି",
  noMarksAtAll: "କୌଣସି ଦାଗ ନାହିଁ",
  anythingNotWorking: "କିଛି କାମ କରୁନାହିଁ କି? ପ୍ରଯୁଜ୍ୟ ସବୁ ବାଛନ୍ତୁ, କିମ୍ବା ସବୁ ଠିକ୍ ଅଛି କୁହନ୍ତୁ।",
  thatIsAllThatIsWrong: "ଏତିକି ହିଁ ଖରାପ",
  everythingWorks: "ସବୁକିଛି କାମ କରୁଛି",
  whichDoYouStillHave: "ଏଥିମଧ୍ୟରୁ କଣ ଏବେ ବି ଆପଣଙ୍କ ପାଖରେ ଅଛି?",
  thatIsWhatIHave: "ମୋ ପାଖରେ ଏହା ଅଛି",
  iHaveNoneOfThese: "ଏଥିମଧ୍ୟରୁ କିଛି ନାହିଁ",
  pincode: "ଶେଷ ପ୍ରଶ୍ନ: ପିକଅପ୍ ପାଇଁ ଆପଣଙ୍କ ଛଅ ଅଙ୍କର ପିନକୋଡ୍ କଣ?",
  pincodePlaceholder: "6 ଅଙ୍କର ପିନକୋଡ୍",
  quote: "ଏଥିପାଇଁ Cashit {amount} ଟଙ୍କା ଦେବ, ଯାଞ୍ଚ ପରେ ନିଶ୍ଚିତ ହେବ। ଆମେ ଆଗକୁ ବଢ଼ିବା କି?",
  composerPlaceholder: "ଉପକରଣର ନାମ ଲେଖନ୍ତୁ, କିମ୍ବା ଯାହା ଇଚ୍ଛା ପଚାରନ୍ତୁ…",
  verifyHeading: "ଆପଣଙ୍କ ମୋବାଇଲ୍ ନମ୍ବର ଯାଞ୍ଚ କରନ୍ତୁ",
  verifyCopy: "ଆପଣଙ୍କ ଦାମ ପ୍ରସ୍ତୁତ। ନିଶ୍ଚିତ କରିବାକୁ ଆମେ ଥରେ ବ୍ୟବହାର ହେଉଥିବା କୋଡ୍ ପଠାଇବୁ, ତା ପରେ ଆପଣଙ୍କ Cashit ଖାତାରେ ବିକ୍ରି ଅନୁରୋଧ ତିଆରି କରିବୁ। ଆପଣ କହିଥିବା ସବୁ ସୁରକ୍ଷିତ ଅଛି।",
  mobileLabel: "ମୋବାଇଲ୍ ନମ୍ବର",
  mobilePlaceholder: "10 ଅଙ୍କର ମୋବାଇଲ୍ ନମ୍ବର",
  sendCode: "କୋଡ୍ ପଠାନ୍ତୁ",
  codeHeading: "କୋଡ୍ ଲେଖନ୍ତୁ",
  codeCopy: "ଆମେ {mobile} କୁ କୋଡ୍ ପଠାଇଛୁ। ଏହା {minutes} ମିନିଟ୍ ବୈଧ।",
  codeLabel: "ଥରେ ବ୍ୟବହାର ହେଉଥିବା କୋଡ୍",
  verifyAndContinue: "ଯାଞ୍ଚ କରି ଆଗକୁ ବଢ଼ନ୍ତୁ",
  resendIn: "{seconds} ସେକେଣ୍ଡରେ ପୁଣି ପଠାନ୍ତୁ",
  resendCode: "କୋଡ୍ ପୁଣି ପଠାନ୍ତୁ",
  changeNumber: "ନମ୍ବର ବଦଳାନ୍ତୁ",
  registerHeading: "ଆପଣଙ୍କ Cashit ଖାତା ତିଆରି କରନ୍ତୁ",
  registerCopy: "{mobile} Cashit ରେ ନୂଆ। କେବଳ ଆପଣଙ୍କ ନାମ କୁହନ୍ତୁ - ଏହି ଖାତା ହିଁ ଆପଣ cashit.in ରେ ବ୍ୟବହାର କରିବେ।",
  fullName: "ପୂରା ନାମ",
  emailOptional: "ଇମେଲ୍ (ଐଚ୍ଛିକ)",
  createAccount: "ଖାତା ତିଆରି କରି ଆଗକୁ ବଢ଼ନ୍ତୁ",
  confirmHeading: "ଆପଣଙ୍କ ବିକ୍ରି ଅନୁରୋଧ ନିଶ୍ଚିତ କରନ୍ତୁ",
  confirmCopy: "ଆପଣ ନିଶ୍ଚିତ ନକଲା ପର୍ଯ୍ୟନ୍ତ କିଛି ତିଆରି ହୁଏ ନାହିଁ। ପିକଅପ୍ ସ୍ଥିର କରିବାକୁ Cashit ଯୋଗାଯୋଗ କରିବ, ଏବଂ ଅନ୍ତିମ ରାଶି ଯାଞ୍ଚ ପରେ ସ୍ଥିର ହେବ।",
  account: "ଖାତା",
  mobile: "ମୋବାଇଲ୍",
  device: "ଉପକରଣ",
  cashitPays: "Cashit ଦେବ",
  placedVia: "ବିକ୍ରିର ମାଧ୍ୟମ",
  pickupAddress: "ପିକଅପ୍ ଠିକଣା",
  differentAddress: "ଅନ୍ୟ ଠିକଣାରୁ ନିଅନ୍ତୁ",
  pickupPincode: "ପିକଅପ୍ ପିନକୋଡ୍",
  area: "ଅଞ୍ଚଳ",
  address: "ଠିକଣା",
  landmarkOptional: "ଲ୍ୟାଣ୍ଡମାର୍କ (ଐଚ୍ଛିକ)",
  whenCollect: "ଆମେ କେବେ ନେବାକୁ ଆସିବୁ?",
  slotMorning: "ସକାଳ · 10 – 1",
  slotAfternoon: "ଅପରାହ୍ନ · 1 – 4",
  slotEvening: "ସନ୍ଧ୍ୟା · 4 – 7",
  today: "ଆଜି",
  tomorrow: "କାଲି",
  visitNote: "Cashit ଆପଣଙ୍କ ସହ ଭେଟ ନିଶ୍ଚିତ କରିବ — ଏଥିରୁ ଏଜେଣ୍ଟ ଜାଣନ୍ତି ଆପଣଙ୍କୁ କେବେ ସୁବିଧା।",
  confirmButton: "ନିଶ୍ଚିତ କରି ବିକ୍ରି ଅନୁରୋଧ ତିଆରି କରନ୍ତୁ",
  doneMessage: "ହୋଇଗଲା। ଆପଣଙ୍କ ବିକ୍ରି ଅନୁରୋଧ {leadNumber}, ଏବଂ ପିକଅପ୍ ପାଇଁ Cashit ଯୋଗାଯୋଗ କରିବ।",
  doneDetail: "ଆପଣଙ୍କ ଅନୁରୋଧ {leadNumber} Cashit ପାଖରେ ଅଛି, {source} ରୁ ପଠାଯାଇଛି। ତା ପରେ ଜଣେ ଏଜେଣ୍ଟ ନିଯୁକ୍ତ ହୋଇ ପିକଅପ୍ କରିବେ; ତା ପରେ Cashit ଉପକରଣ ଯାଞ୍ଚ କରି ଆପଣଙ୍କୁ ଦେୟ ଦେବ।",
  trackSale: "ଏହି ବିକ୍ରିକୁ ଟ୍ରାକ୍ କରନ୍ତୁ",
  trackSaleHint: "ସ୍ଥିତି ଓ ପରବର୍ତ୍ତୀ ପଦକ୍ଷେପ ଦେଖନ୍ତୁ",
}

const TABLES: Record<string, Phrases> = { en, hi, mr, ur, bn, ta, te, kn, gu, pa, ml, or }

/** The phrases for a language, falling back to English for anything not translated. */
export function phrases(language?: string): Phrases {
  if (!language) return en
  return TABLES[language.toLowerCase()] ?? en
}

/**
 * The language of something the customer wrote, worked out from its SCRIPT.
 *
 * Script, not language: it is the one signal available in the browser that is both free and reliable.
 * Devanagari is read as Hindi, which is right far more often than it is wrong on this site, and is anyway
 * only used to choose which of the sentences above to show - never to decide anything about the sale.
 *
 * Latin returns nothing at all, deliberately. A customer typing Hinglish - "mera iphone bechna hai" - is
 * writing in Latin script, and guessing Hindi from that would put Devanagari questions in front of
 * somebody who chose not to use it. The assistant still answers them in Hinglish, because it reads the
 * words rather than the alphabet.
 */
export function languageOfScript(text: string): string | undefined {
  if (/[஀-௿]/.test(text)) return 'ta'
  if (/[ఀ-౿]/.test(text)) return 'te'
  if (/[ಀ-೿]/.test(text)) return 'kn'
  if (/[ഀ-ൿ]/.test(text)) return 'ml'
  if (/[઀-૿]/.test(text)) return 'gu'
  if (/[਀-੿]/.test(text)) return 'pa'
  // Odia before Bengali: their Unicode blocks are neighbours, and reading one as the other would answer
  // an Odia speaker in Bengali.
  if (/[଀-୿]/.test(text)) return 'or'
  if (/[ঀ-৿]/.test(text)) return 'bn'
  if (/[؀-ۿ]/.test(text)) return 'ur'
  if (/[ऀ-ॿ]/.test(text)) return 'hi'
  return undefined
}

/** Fills {name} placeholders. Kept trivial on purpose - these strings take one or two values each. */
export function fill(template: string, values: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (whole, key) => (key in values ? String(values[key]) : whole))
}
